import { useState, useEffect } from 'react';
import { findArtistByName, getArtistTopTracks } from '../api/deezerClient';
import { getArtistInfo } from '../api/lastfmClient';
import { trackLinkKey } from '../api/itunesClient';
import { useAppleMusicSongLinks } from '../hooks/useAppleMusicSongLinks';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { unslugifyArtist, slugifyArtist, buildMusicSearchUrl, DEFAULT_MUSIC_SERVICE, MUSIC_SERVICE_LABELS } from '../utils/exportUtils';
import { useAuth } from '../state/AuthContext';
import ArtistActionPills from './ArtistActionPills';
import AuthModal from './auth/AuthModal';
import MusicServiceIcon from './MusicServiceIcon';
import '../styles/artistLanding.css';

function ArtistLandingPage({ slug }) {
  const { user } = useAuth();
  const musicService = user?.musicService || DEFAULT_MUSIC_SERVICE;
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState(null);
  const [listeners, setListeners] = useState(null);
  const [loading, setLoading] = useState(true);
  const audio = useAudioPreview();

  // Canonical Apple Music links, filled in as background lookups resolve
  const appleMusicLinks = useAppleMusicSongLinks(tracks, musicService);

  const displayName = unslugifyArtist(slug);

  // Artists this recommendation is connected to, passed via the notification
  // deep link (?connected=A::B::C). Empty for plain SEO visits.
  const [connectedArtists] = useState(() => {
    const param = new URLSearchParams(window.location.search).get('connected');
    return param ? param.split('::').map((s) => s.trim()).filter(Boolean).slice(0, 3) : [];
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [deezerArtist, lastfmInfo] = await Promise.all([
          findArtistByName(displayName, { priority: true }),
          getArtistInfo(displayName).catch(() => null),
        ]);
        if (cancelled) return;

        if (lastfmInfo?.listeners) setListeners(lastfmInfo.listeners);

        if (deezerArtist) {
          setArtist(deezerArtist);
          const topTracks = await getArtistTopTracks(deezerArtist.id, 5, { priority: true });
          if (!cancelled) setTracks(topTracks);
        }
      } catch {
        // Silently fail — page still shows artist name and Spotify link
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [displayName]);

  const artistName = artist?.name || displayName;
  const artistImage = artist?.imageLarge || artist?.image;

  return (
    <div className="artist-landing">
      <header className="artist-landing-header">
        <a href="/" className="share-logo">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          </svg>
          Stellar
        </a>
        <a href="/" className="share-cta-link">Discover music</a>
      </header>

      <main className="artist-landing-content">
        {loading ? (
          <div className="artist-landing-loading">
            <div className="share-loading-spinner" />
            <span>Loading artist...</span>
          </div>
        ) : (
          <>
            <div className="artist-landing-hero">
              <div className="artist-landing-image-wrapper">
                {artistImage ? (
                  <img className="artist-landing-image" src={artistImage} alt={artistName} />
                ) : (
                  <div className="artist-landing-image artist-landing-placeholder">
                    {artistName.charAt(0)}
                  </div>
                )}
              </div>

              <h1 className="artist-landing-name">{artistName}</h1>

              {listeners > 0 && (
                <p className="artist-landing-fans">
                  {listeners.toLocaleString()} listeners
                </p>
              )}

              <div className="artist-landing-cta">
                <a
                  href={`/similar-to/${slugifyArtist(artistName)}`}
                  className="share-cta-btn artist-landing-similar-btn"
                >
                  Find similar artists
                </a>
                <a href="/" className="share-cta-btn">Explore on Stellar</a>
              </div>

              <div className="artist-landing-pills">
                <ArtistActionPills
                  artistName={artistName}
                  artistId={artist?.id}
                  artistImage={artistImage}
                />
              </div>
            </div>

            {connectedArtists.length > 0 && (
              <div className="artist-landing-connected">
                <h2 className="artist-landing-connected-title">Connected to your universe</h2>
                <div className="artist-landing-connected-list">
                  {connectedArtists.map((name) => (
                    <a
                      key={name}
                      href={`/artist/${slugifyArtist(name)}`}
                      className="artist-landing-connected-chip"
                    >
                      {name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {tracks && tracks.length > 0 && (
              <div className="artist-landing-tracks">
                <h2 className="artist-landing-tracks-title">Top Songs</h2>
                {tracks.map((track) => {
                  const isPlaying = audio.isPlaying && audio.currentTrack?.id === track.id;
                  return (
                    <div key={track.id} className="artist-landing-track-row">
                      {track.albumImage ? (
                        <img className="artist-landing-track-art" src={track.albumImage} alt="" loading="lazy" />
                      ) : (
                        <div className="artist-landing-track-art-placeholder" />
                      )}
                      <div className="artist-landing-track-info">
                        <span className="artist-landing-track-name">{track.name}</span>
                        <span className="artist-landing-track-album">{track.albumName}</span>
                      </div>
                      <div className="artist-landing-track-actions">
                        {track.previewUrl && (
                          <button
                            className={`artist-landing-track-play ${isPlaying ? 'playing' : ''}`}
                            onClick={() => isPlaying ? audio.pause() : audio.play(track)}
                            title={isPlaying ? 'Pause' : 'Play preview'}
                          >
                            {audio.currentTrack?.id === track.id && audio.progress > 0 && (
                              <svg className="preview-progress-ring preview-progress-ring--sm" viewBox="0 0 40 40">
                                <circle
                                  cx="20" cy="20" r="18"
                                  fill="none"
                                  stroke="rgba(129, 140, 248, 0.25)"
                                  strokeWidth="2"
                                />
                                <circle
                                  cx="20" cy="20" r="18"
                                  fill="none"
                                  stroke="#818cf8"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeDasharray={2 * Math.PI * 18}
                                  strokeDashoffset={2 * Math.PI * 18 * (1 - (audio.progress || 0))}
                                />
                              </svg>
                            )}
                            {isPlaying ? (
                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <rect x="6" y="4" width="4" height="16" />
                                <rect x="14" y="4" width="4" height="16" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <polygon points="6,3 20,12 6,21" />
                              </svg>
                            )}
                          </button>
                        )}
                        <a
                          className={`artist-landing-track-spotify${musicService === 'apple-music' ? ' apple-music' : ''}`}
                          href={appleMusicLinks.get(trackLinkKey(track)) || buildMusicSearchUrl(track, musicService)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Search in ${MUSIC_SERVICE_LABELS[musicService]}`}
                        >
                          <MusicServiceIcon service={musicService} size={16} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      <AuthModal />
    </div>
  );
}

export default ArtistLandingPage;
